import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { openChannelByEpisodeChatChannelsByEpisodeEpisodeIdPost } from '../../services/api';
import type { ChannelHeader } from '../../services/api';
import { useStore } from '../../store';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { chatKeys } from './useChatQueries';

const chartPatientId = (pathname: string) =>
  pathname.match(/^\/patients\/([^/]+)/)?.[1];

/**
 * The dock press (and Cmd/Ctrl+.). Opens the dock (or the sheet's inbox) at once. From a patient
 * chart with an episode selected it then asks the backend for that episode's
 * care-team conversation, creating it for a care-team member, and
 * opens it as a window beside the dock. The dock stays alone for any other
 * answer, so a failure is never worse than the press was before.
 */
export function useOpenChatFromChart(): () => void {
  const queryClient = useQueryClient();
  const openChatInbox = useStore(state => state.openChatInbox);
  const openChatWindow = useStore(state => state.openChatWindow);
  const minimizeChatWindow = useStore(state => state.minimizeChatWindow);

  // The route is read when the answer arrives, not when the button is pressed:
  // `chatContextEpisodeId` outlives the chart, so a late answer for an earlier
  // chart must not open its conversation.
  const { pathname } = useLocation();
  const pathnameRef = useRef(pathname);
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  return useCallback(() => {
    openChatInbox();
    // The windows that were open at the press: a person who opens another one
    // before the answer arrives must keep it (and its focus).
    const openAtPress = new Set(useStore.getState().chatWindows.open);
    const episodeId = useStore.getState().chatContextEpisodeId;
    if (!episodeId || !chartPatientId(pathnameRef.current)) return;

    openChannelByEpisodeChatChannelsByEpisodeEpisodeIdPost({
      path: { episode_id: episodeId },
      throwOnError: true,
    })
      .then(({ data, status }) => {
        const header = data as ChannelHeader;
        queryClient.setQueryData(chatKeys.careTeam(episodeId), header);
        if (status === 201) {
          void queryClient.invalidateQueries({ queryKey: chatKeys.inbox() });
        }
        const {
          chatLayout,
          chatWindows,
          chatSheetOpen,
          chatSheetView,
          chatContextEpisodeId,
        } = useStore.getState();
        // Still where the press left it: the dock open, or the sheet on its
        // inbox. A person who closed either has moved on.
        const surfaceOpen =
          chatLayout === 'sheet'
            ? chatSheetOpen && chatSheetView === 'inbox'
            : chatWindows.dockOpen;
        const currentPatient = chartPatientId(pathnameRef.current);
        if (
          surfaceOpen &&
          chatContextEpisodeId === episodeId &&
          header.patient_id === currentPatient
        ) {
          const { open } = chatWindows;
          const movedOn =
            !open.includes(header.id) && open.some(id => !openAtPress.has(id));
          // A tab, not a window: the answer must not replace what they opened
          // or take focus from it.
          if (movedOn) minimizeChatWindow(header.id);
          else openChatWindow(header.id);
        }
      })
      .catch((error: unknown) => {
        const status = getHttpStatus(error);
        if (status === 404 || status === 403) return;
        console.error('Failed to open the care-team conversation', error);
      });
  }, [openChatInbox, openChatWindow, minimizeChatWindow, queryClient]);
}
